import { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import { UnknownAction } from "@reduxjs/toolkit";
import { setPreviousPath } from "./appSlice";

const getFullPath = () => window.location.pathname + window.location.search;

const useLocationTracker = ({ dispatch }: { dispatch: Dispatch<UnknownAction> }) => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  // The CURRENT route's pathname+search (filters/sort/date range/page), so a
  // later route change can hand the PREVIOUS route's full URL to `BackButton`
  // — not just its bare pathname. A ref (not state): most filter/sort/
  // pagination writes change only the search string, and updating a ref
  // doesn't retrigger the effect below (which only needs to resubscribe on a
  // real pathname change).
  const currentFullPathRef = useRef(getFullPath());

  const listener = useCallback(() => {
    const fullPath = getFullPath();
    if (window.location.pathname === currentPath) {
      // Same route: keep the latest search in sync so the NEXT real route
      // change captures this route's up-to-date params, not whatever they
      // were when this route was first entered.
      currentFullPathRef.current = fullPath;
      return;
    }
    dispatch(setPreviousPath(currentFullPathRef.current));
    setCurrentPath(window.location.pathname);
    currentFullPathRef.current = fullPath;
  }, [currentPath, dispatch]);

  useEffect(() => {
    // Listen to browser back/forward buttons
    window.addEventListener("popstate", listener);
    // Intercept programmatic navigation (React Router uses these)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      listener();
    };
    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      listener();
    };
    return () => {
      window.removeEventListener("popstate", listener);
      // Restore original methods to prevent infinite wrapper chain memory leak
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [listener]);
};

export default useLocationTracker;
